import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '../UI/card';
import { Button } from '../UI/button';
import { CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, Download } from 'lucide-react';

/**
 * RÉSUMÉ DES RACHATS (bulk import)
 * Miroir de PaymentSummary, adapté aux colonnes rachats :
 *  - N° rachat, Matricule, Nom, Prénoms, Type, Date, Net payé, Motif/Statut
 */

const fmt = (amount) => {
  const n = Number(amount);
  return isNaN(n) ? '-' : new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';
};

const toDateStr = (d) => {
  if (!d) return '-';
  const x = new Date(d);
  return isNaN(x) ? String(d) : x.toISOString().slice(0, 10);
};

const downloadXLSX = (filename, headers, rows) => {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const colWidths = headers.map((h, i) => ({
    wch: Math.max(h.length, ...rows.map(r => String(r[i] ?? '').length), 10)
  }));
  ws['!cols'] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Résultats');
  XLSX.writeFile(wb, filename);
};

const Section = ({ title, icon, count, colorClass, onDownload, children }) => {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <Card className={`border ${colorClass.border}`}>
      <CardHeader className={`py-3 px-4 ${colorClass.header}`}>
        <CardTitle className={`text-base flex items-center justify-between ${colorClass.title}`}>
          <span
            className="flex items-center gap-2 cursor-pointer select-none flex-1"
            onClick={() => setOpen(o => !o)}
          >
            {icon}
            {title} ({count})
            {open ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </span>
          {onDownload && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 gap-1 text-xs"
              onClick={(e) => { e.stopPropagation(); onDownload(); }}
            >
              <Download className="w-3 h-3" /> Excel
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      {open && <CardContent className="p-0">{children}</CardContent>}
    </Card>
  );
};

const WithdrawalSummary = ({ results, validationRows = [] }) => {
  if (!results) return null;

  const {
    totalProcessed = 0,
    successCount = 0,
    errorCount = 0,
    payments = [],
    errors = []
  } = results;

  const warnings    = errors.filter(e => e.warning);
  const realErrors  = errors.filter(e => !e.warning);
  const invalidRows = validationRows.filter(r => !r.valid && !(r.warnings || []).some(w => /déjà/i.test(w)));

  const successRate = totalProcessed > 0
    ? ((successCount / totalProcessed) * 100).toFixed(1)
    : '0.0';

  const allSuccess = successCount === totalProcessed && invalidRows.length === 0 && warnings.length === 0;

  const HEADERS = ['N° rachat', 'Matricule', 'Nom', 'Prénoms', 'Type', 'Date', 'Net payé', 'Motif / Statut'];

  const dlProcessed = () => downloadXLSX(
    'rachats_traites.xlsx', HEADERS,
    payments.map(p => [
      p.withdrawalNumber || '',
      p.mutualiste?.matriculeinterne || p.mutualiste?.matricule || '',
      p.mutualiste?.nom || '',
      p.mutualiste?.prenoms || '',
      p.status || '',
      toDateStr(p.paidAt),
      p.amount ?? '',
      p.receiptNumber || ''
    ])
  );

  const dlIgnored = () => downloadXLSX(
    'rachats_ignores.xlsx', HEADERS,
    warnings.map(w => [
      w.racNumber || '',
      w.matricule || '',
      w.nom || '',
      w.prenoms || '',
      w.withdrawalType || '',
      toDateStr(w.date),
      w.montant ?? '',
      w.error
    ])
  );

  const dlErrors = () => downloadXLSX(
    'rachats_erreurs.xlsx', HEADERS,
    realErrors.map(e => [
      e.racNumber || '',
      e.matricule || '',
      e.nom || '',
      e.prenoms || '',
      e.withdrawalType || '',
      toDateStr(e.date),
      e.montant ?? '',
      e.error
    ])
  );

  const dlInvalid = () => downloadXLSX(
    'rachats_invalides.xlsx', HEADERS,
    invalidRows.map(r => [
      r.data?.racNumber || '',
      r.data?.matricule || '',
      r.data?.nom || '',
      r.data?.prenoms || '',
      r.data?.withdrawalType || '',
      r.data?.date || '',
      r.data?.netAmount ?? '',
      (r.errors || []).join(' | ')
    ])
  );

  const renderTable = (rows, colors) => (
    <div className="max-h-80 overflow-y-auto">
      <table className="min-w-full text-sm">
        <thead className={`${colors.header} sticky top-0 z-10`}>
          <tr>
            {HEADERS.map(h => (
              <th key={h} className={`px-3 py-2 text-left font-medium ${colors.th} whitespace-nowrap`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className={`divide-y ${colors.divider}`}>
          {rows.map((row, i) => (
            <tr key={i} className={colors.row}>
              <td className="px-3 py-2 font-mono text-xs">{row.racNumber}</td>
              <td className="px-3 py-2 font-mono text-xs">{row.matricule}</td>
              <td className="px-3 py-2">{row.nom}</td>
              <td className="px-3 py-2">{row.prenoms}</td>
              <td className="px-3 py-2 text-xs">{row.type}</td>
              <td className="px-3 py-2 text-xs font-mono">{row.date}</td>
              <td className="px-3 py-2 text-right">{row.amount}</td>
              <td className="px-3 py-2 text-xs">{row.motif}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Bandeau résumé */}
      <Card className={allSuccess ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}>
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            {allSuccess
              ? <CheckCircle className="w-10 h-10 text-green-600 shrink-0" />
              : <AlertCircle className="w-10 h-10 text-orange-600 shrink-0" />}
            <div className="flex-1">
              <p className="font-bold text-lg">
                {allSuccess ? 'Traitement terminé avec succès' : 'Traitement terminé'}
              </p>
              <p className="text-sm text-muted-foreground">
                {successCount} rachat{successCount !== 1 ? 's' : ''} traité{successCount !== 1 ? 's' : ''} sur {totalProcessed}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-2xl font-bold">{successRate}%</p>
              <p className="text-xs text-muted-foreground">taux de succès</p>
            </div>
          </div>
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                parseFloat(successRate) >= 80 ? 'bg-green-500'
                  : parseFloat(successRate) >= 50 ? 'bg-orange-500' : 'bg-red-500'
              }`}
              style={{ width: `${successRate}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Compteurs */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-3 text-center">
            <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-green-700">{successCount}</p>
            <p className="text-xs text-green-600">Traités</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3 text-center">
            <AlertCircle className="w-5 h-5 text-amber-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-700">{warnings.length}</p>
            <p className="text-xs text-amber-600">Ignorés</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-3 text-center">
            <XCircle className="w-5 h-5 text-red-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-red-700">{realErrors.length}</p>
            <p className="text-xs text-red-600">Erreurs</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="p-3 text-center">
            <XCircle className="w-5 h-5 text-gray-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-700">{invalidRows.length}</p>
            <p className="text-xs text-gray-500">Invalides</p>
          </CardContent>
        </Card>
      </div>

      {/* Rachats traités */}
      <Section
        title="Rachats traités"
        icon={<CheckCircle className="w-4 h-4" />}
        count={successCount}
        colorClass={{ border: 'border-green-200', header: 'bg-green-50', title: 'text-green-700' }}
        onDownload={dlProcessed}
      >
        {renderTable(
          payments.map(p => ({
            racNumber: p.withdrawalNumber || '-',
            matricule: p.mutualiste?.matriculeinterne || p.mutualiste?.matricule || '-',
            nom:       p.mutualiste?.nom || '-',
            prenoms:   p.mutualiste?.prenoms || '',
            type:      p.status || '-',
            date:      toDateStr(p.paidAt),
            amount:    p.amount != null ? fmt(p.amount) : '-',
            motif:     p.receiptNumber || '-'
          })),
          { border: 'border-green-200', header: 'bg-green-50', th: 'text-green-800', divider: 'divide-green-100', row: 'hover:bg-green-50' }
        )}
      </Section>

      {/* Lignes ignorées */}
      <Section
        title="Rachats ignorés (déjà importés)"
        icon={<AlertCircle className="w-4 h-4" />}
        count={warnings.length}
        colorClass={{ border: 'border-amber-200', header: 'bg-amber-50', title: 'text-amber-700' }}
        onDownload={dlIgnored}
      >
        {renderTable(
          warnings.map(w => ({
            racNumber: w.racNumber || '-',
            matricule: w.matricule || '-',
            nom:       w.nom || '-',
            prenoms:   w.prenoms || '',
            type:      w.withdrawalType || '-',
            date:      toDateStr(w.date),
            amount:    w.montant != null ? fmt(w.montant) : '-',
            motif:     w.error
          })),
          { border: 'border-amber-200', header: 'bg-amber-50', th: 'text-amber-800', divider: 'divide-amber-100', row: 'hover:bg-amber-50' }
        )}
      </Section>

      {/* Erreurs de traitement */}
      <Section
        title="Erreurs de traitement"
        icon={<XCircle className="w-4 h-4" />}
        count={realErrors.length}
        colorClass={{ border: 'border-red-200', header: 'bg-red-50', title: 'text-red-700' }}
        onDownload={dlErrors}
      >
        {renderTable(
          realErrors.map(e => ({
            racNumber: e.racNumber || '-',
            matricule: e.matricule || '-',
            nom:       e.nom || '-',
            prenoms:   e.prenoms || '',
            type:      e.withdrawalType || '-',
            date:      toDateStr(e.date),
            amount:    e.montant != null ? fmt(e.montant) : '-',
            motif:     e.error
          })),
          { border: 'border-red-200', header: 'bg-red-50', th: 'text-red-800', divider: 'divide-red-100', row: 'hover:bg-red-50' }
        )}
      </Section>

      {/* Lignes invalides */}
      <Section
        title="Lignes invalides (rejetées à la validation)"
        icon={<XCircle className="w-4 h-4" />}
        count={invalidRows.length}
        colorClass={{ border: 'border-gray-200', header: 'bg-gray-50', title: 'text-gray-700' }}
        onDownload={dlInvalid}
      >
        {renderTable(
          invalidRows.map(r => ({
            racNumber: r.data?.racNumber || '-',
            matricule: r.data?.matricule || '-',
            nom:       r.data?.nom || '-',
            prenoms:   r.data?.prenoms || '',
            type:      r.data?.withdrawalType || '-',
            date:      r.data?.date || '-',
            amount:    r.data?.netAmount != null ? fmt(r.data?.netAmount) : '-',
            motif:     (r.errors || []).join(' · ')
          })),
          { border: 'border-gray-200', header: 'bg-gray-50', th: 'text-gray-600', divider: 'divide-gray-100', row: 'hover:bg-gray-50' }
        )}
      </Section>
    </div>
  );
};

export default WithdrawalSummary;
